/**
 * ESCHER FALLS — アプリ内課金の差し口。
 *
 * **StoreKit も課金プラグインも入っていない。** 入っているのは境界だけである。
 * `rewardedAd.ts` と同じ作りで、後から `setPurchaseProvider()` で差し込む。
 *
 * ## なぜ中身が空なのか
 * 課金は**有料の Apple Developer Program と App Store Connect の商品登録**が
 * 揃うまで、動かすことも試すことも一切できない。揃う前にプラグインを入れると、
 * 動かない購入経路がゲームに混じったまま寝ることになる。
 * いまの状態（未加入）ではそれが検証もできない死んだコードになる。
 *
 * ## 守る規則
 * 1. **提供者が居なければ、買う入口を UI に出さない。**
 *    押しても何も起きないボタンは壊れているのと同じ（広告と同じ規則）。
 * 2. **購入は失敗しうる。** 取り消し・通信断・審査中の商品。
 *    失敗したら何も渡さないが、遊びは何事も無かったように続くこと。
 * 3. **消耗品（雫）と非消耗品（広告除去）を混ぜない。**
 *    非消耗品は「復元」に応じる義務がある（ガイドライン 3.1.1）。
 *    復元の口 `restore()` を最初から型に入れてあるのはこのため。
 * 4. **値段をコードに書かない。** 実際の表示価格はストアが地域ごとに返す。
 *    ここに書いた `yen` は目安で、UI は provider が返した文字列を優先する。
 */

import { t } from '../ui/i18n';

/** 商品の種類。**扱いが根本的に違うので必ず分ける。** */
export type ProductKind = 'consumable' | 'nonConsumable';

export interface Product {
  /** App Store Connect に登録する商品 ID。 */
  readonly id: string;
  readonly kind: ProductKind;
  readonly name: string;
  /** 消耗品なら渡す雫の数。非消耗品では 0。 */
  readonly drops: number;
  /** 目安の価格（円）。実際の表示はストアが返すものを優先する。 */
  readonly yen: number;
  /**
   * 売り場に出さない。**登録は残すが単体では売らない**商品に立てる。
   *
   * 消してしまうと、過去に買った人の復元先が無くなる。
   * 商品 ID は再利用できないので、消すのではなく伏せる。
   */
  readonly hidden?: boolean;
}

/**
 * 商品一覧。
 *
 * 雫の値段は「使い道の相場」から決めてある。時間延長が雫40なので、
 * 一番小さい束（600）でおよそ15回ぶん。参照した既存ゲームの
 * コイン束（¥500〜¥1,300）と同じ桁に収まるようにした。
 */
export const PRODUCTS: readonly Product[] = [
  { id: 'info.youmix.escherfalls.drops600', kind: 'consumable', name: '雫 600', drops: 600, yen: 300 },
  { id: 'info.youmix.escherfalls.drops1600', kind: 'consumable', name: '雫 1600', drops: 1600, yen: 600 },
  { id: 'info.youmix.escherfalls.drops5000', kind: 'consumable', name: '雫 5000', drops: 5000, yen: 1300 },
  // **単体では売らない。** 消せる広告は本編で数回だけなので、
  // 単品の商品として成立しない（買っても消えるものがほとんど無い）。
  // エクストラを買うと一緒に外れる。登録だけ残すのは、商品 ID を
  // 再利用できないためと、過去に買った人の復元先を残すため。
  { id: 'info.youmix.escherfalls.noads', kind: 'nonConsumable', name: '広告を消す', drops: 0, yen: 500, hidden: true },
  // エクストラ一式。**非消耗品**なので復元の対象（買い直させない）。
  //
  // ## 「第一集」と名付けてある
  // Monument Valley は追加パックを出したとき「既存購入者には無料で
  // 寄越せ」という星1レビューを受けた。**最初から番号を振っておけば、
  // 第二集は裏切りではなく予定になる。**
  //
  // ## ¥600 の根拠
  // Monument Valley の追加パックは**すでに $3.99 払った人**に $2 で8面を
  // 売って購入率10%。こちらは一銭も払っていない人に20面を売る。
  // 面あたりは安くても**最初の一歩としての額**が効くので、
  // 参照した Fill のモード解放（¥600）に合わせた。
  { id: 'info.youmix.escherfalls.extras', kind: 'nonConsumable', name: 'エクストラ 第一集 21面（広告なし）', drops: 0, yen: 600 },
];

/** 広告除去の商品 ID。**エクストラを買っても同じ効果になる。** */
export const NOADS_PRODUCT_ID = 'info.youmix.escherfalls.noads';

/** 売り場に並べる商品。伏せてあるものは出さない。 */
export const SHOP_PRODUCTS: readonly Product[] = PRODUCTS.filter((p) => p.hidden !== true);

/** エクストラ解放の商品 ID。購入の処理で名指しするので定数にしておく。 */
export const EXTRAS_PRODUCT_ID = 'info.youmix.escherfalls.extras';

export function productById(id: string): Product | undefined {
  return PRODUCTS.find((p) => p.id === id);
}

export interface PurchaseProvider {
  /** 買える状態か。商品情報の取得が済んでいなければ false。 */
  isReady(): boolean;
  /**
   * ストアが返す表示価格。取れなければ null（UI は目安の円を出す）。
   * 地域ごとに通貨も額も違うので、**コードの数字を表示に使わない。**
   */
  priceOf(productId: string): string | null;
  /** 買う。**買えたときだけ true。** 取り消し・失敗では false。例外は投げない。 */
  purchase(productId: string): Promise<boolean>;
  /** 非消耗品の復元。買い直させないための義務（ガイドライン 3.1.1）。 */
  restore(): Promise<readonly string[]>;
}

let provider: PurchaseProvider | null = null;

export function setPurchaseProvider(next: PurchaseProvider | null): void {
  provider = next;
}

/** 買う入口を UI に出してよいか。**出す側は必ずこれを見ること。** */
export function isStoreAvailable(): boolean {
  return provider !== null && provider.isReady();
}

/** 表示価格。提供者が無ければ目安の円を返す。 */
/**
 * 売り場に出す商品名。
 *
 * **ストアが返す名前は使わない。** StoreKit の `displayName` は
 * App Store Connect のロケールに従うが、こちらは端末の言語で出す。
 * 二つが食い違うと（日本語端末で英語ストアを見ているときなど）
 * 画面の中で言語が混ざる。
 */
export function productName(p: Product): string {
  const key = p.id.slice(p.id.lastIndexOf('.') + 1);
  return t.product[key] ?? p.name;
}

export function displayPrice(p: Product): string {
  return provider?.priceOf(p.id) ?? `¥${p.yen}`;
}

/** 買う。買えたら true。**失敗しても投げない。** */
export async function purchase(productId: string): Promise<boolean> {
  if (provider === null) return false;
  try {
    return await provider.purchase(productId);
  } catch {
    return false;
  }
}

/** 復元。買い直しを強いないための口。 */
export async function restore(): Promise<readonly string[]> {
  if (provider === null) return [];
  try {
    return await provider.restore();
  } catch {
    return [];
  }
}
